/**
 * Insere funcionalidades adicionais nos objetos Button
 *
 * @author Wellington
 * @since 2025-05-22
 * @version 1.0.0
 * @license Comercial
 */

import Format from '../tools/format';
import setIcon from "../components/define/setIcon";
import setMask from "../components/define/setMask";
import { sanitizeHtml } from '../../shared/core/master/htmlSanitizer';
import { resolveKnownImageSource } from '../components/create/image';

const resolveCellValue = (value, data = {}) => {
  if (typeof value !== 'string') return value;
  if (value.startsWith('$')) return data[value.slice(1)];
  return value.replace(/\{([\w.]+)\}/g, (_, key) => String(data?.[key] ?? ''));
};

const safeHref = (value, internal = false) => {
  const href = String(value ?? '').trim();
  if (!href || /^\s*(javascript|data):/i.test(href)) return '';
  if (internal) return href.startsWith('/') ? href : '';
  try {
    const url = new URL(href, window.location.origin);
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol) ? href : '';
  } catch { return ''; }
};

const htmlTableCellElementExtensions = {
  /**
   * 
   */
  addContent(options) {
    this.empty();
    const hasOwnText = options.text !== undefined && options.text !== null;
    const hasLabel = options.label !== undefined && options.label !== null;

    if (hasOwnText || hasLabel) {
      if (!hasOwnText) {
        options.text = options.label;
      }

      if (options.icon && options.icon.position !== 'right') {
        setIcon(this, options.icon);
      }

      this.addText(options.text, options);

      if (options.icon && options.icon.position === 'right') {
        setIcon(this, options.icon);
      }
    }
    return this;
  },

  addCurrency(text, options = { decimalPlaces: 2, percent: false, rules: false }) {
    if (!options.decimalPlaces) options.decimalPlaces = 2;
    if (!options.currency) options.currency = false;
    if (!options.percent) options.percent = false;
    if (!options.rules) options.rules = false;

    const format = new Format();
    text = format.number(text, options);
    this.addText(text);
  },

  addDate(text) {
    const format = new Format();
    text = format.date(text);
    this.addText(text, 'date');
  },

  addInteger(text, options = { integer: true, percent: false, rules: false }) {
    options.decimalPlaces = 0;
    options.integer = true;
    options.currency = false;

    if (!options.percent) options.percent = false;
    if (!options.rules) options.rules = false;

    const format = new Format();
    text = format.number(text, options);
    this.addText(text);
  },

  addNumber(text, options = { decimalPlaces: 2, percent: false, rules: false }) {
    if (!options.decimalPlaces) options.decimalPlaces = 2;
    if (!options.currency) options.currency = false;
    if (!options.percent) options.percent = false;
    if (!options.rules) options.rules = false;

    const format = new Format();
    text = format.number(text, options);
    this.addText(text);
  },

  addText(text, options = {}) {
    const wrap = document.createElement('div');
    this.appendChild(wrap);

    if (this.tagName === 'TH') {
      const span1 = document.createElement('span');
      span1.textContent = text;
      
      if (options.key) {
        this.dataset.key = options.key;
      }
      
      wrap.appendChild(span1);

      if (options.sortable) {
        const span2 = document.createElement('span');
        span2.classList.add('index-sort');
        wrap.appendChild(span2);

        const sortUp = document.createElement('i');
        sortUp.classList.add('th-sort-up');
        wrap.appendChild(sortUp);

        const sortDown = document.createElement('i');
        sortDown.classList.add('th-sort-down');
        wrap.appendChild(sortDown);
      }
    } else {
      const div = document.createElement('div');
      const maskTemplate = String(options?.mask?.template || '').trim().toLowerCase();
      const isHtml = maskTemplate === 'html';
      const isIcon = maskTemplate === 'icon';
      const presentation = options?.presentation || options?.mask || {};
      if (maskTemplate === 'link' || presentation.type === 'link') {
        const href = safeHref(resolveCellValue(presentation.href ?? presentation.url ?? text, options.data), presentation.internal === true);
        const link = document.createElement('a');
        link.textContent = String(resolveCellValue(presentation.text ?? text, options.data) ?? '');
        if (href) {
          link.href = href;
          if (presentation.target) link.target = presentation.target;
          if (presentation.target === '_blank') link.rel = 'noopener noreferrer';
        } else link.setAttribute('aria-disabled', 'true');
        div.appendChild(link);
      } else if (maskTemplate === 'image' || presentation.type === 'image') {
        div.classList.add('dg-cell-presentation');
        const source = resolveKnownImageSource(resolveCellValue(presentation.src ?? presentation.url ?? text, options.data));
        const src = safeHref(source);
        if (src) {
          const image = document.createElement('img');
          image.src = src;
          image.alt = String(resolveCellValue(presentation.alt ?? '', options.data));
          image.className = 'dg-cell-image';
          if (Number.isFinite(Number(presentation.width))) image.width = Number(presentation.width);
          if (Number.isFinite(Number(presentation.height))) image.height = Number(presentation.height);
          div.appendChild(image);
        }
        const label = resolveCellValue(presentation.text, options.data);
        if (label !== undefined && label !== null) div.append(document.createTextNode(String(label)));
      } else if (['flag', 'country'].includes(maskTemplate) || presentation.type === 'flag') {
        div.classList.add('dg-cell-presentation');
        const source = presentation.flag === true
          ? text
          : presentation.flag ?? presentation.country ?? presentation.iso2 ?? presentation.field ?? text;
        const iso2 = String(resolveCellValue(typeof source === 'string' && presentation.field ? `$${source}` : source, options.data) ?? '').trim().toLowerCase();
        if (/^[a-z]{2}$/.test(iso2)) setIcon(div, { icon: { type: 'ep', name: `flags ${iso2}` } });
        const label = resolveCellValue(presentation.text ?? text, options.data);
        if (label !== undefined && label !== null && String(label).trim()) div.append(document.createTextNode(String(label)));
      } else if (!isNaN(text) && options.rules) {
        if (options.rules === true) {
          options.rules = [{ color: 'green', rule: '>= 0', icon: 'f062' }, { color: 'red', rule: '< 0', icon: 'f063' }];
        }
        this.applyConditionalFormatting(div, text, options.rules);
      } else if (isHtml) {
        div.innerHTML = sanitizeHtml(String(text ?? ''));
      } else if (isIcon) {
        let iconValue = text;
        if (typeof iconValue === 'string') {
          try {
            iconValue = JSON.parse(iconValue);
          } catch {
            iconValue = null;
          }
        }
        const declaredIcon = presentation.icon || presentation;
        const iconName = String(iconValue?.name || iconValue?.icon_name || declaredIcon?.name || '').trim();
        if (iconName) {
          div.classList.add('dg-cell-presentation');
          const colorField = String(options?.mask?.colorField || '').trim();
          const iconColor = iconValue?.color
            ?? declaredIcon?.color
            ?? (colorField ? options?.data?.[colorField] : null);
          setIcon(div, {
            icon: {
              type: String(iconValue?.type || iconValue?.icon_type || declaredIcon?.type || 'solid').trim() || 'solid',
              name: iconName,
              class: 'dg-cell-icon',
              ...(iconColor ? { color: iconColor } : {}),
            }
          });
          const label = resolveCellValue(presentation.text, options.data);
          if (label !== undefined && label !== null) div.append(document.createTextNode(String(label)));
        }
      } else {
        div.textContent = String(text ?? '').stripTags();
      }

      wrap.appendChild(div);
      if (options?.mask && !isHtml && !['icon', 'link', 'image', 'flag', 'country'].includes(maskTemplate)) {
        setMask(div, options);
      }
    }
  },

  applyConditionalFormatting(div, value, rules) {
    const num = parseFloat(value);
    if (isNaN(num)) return;

    for (const { rule, color, icon } of rules) {
      const test = this.parseCondition(rule);
      if (test(num)) {
        div.classList.add(`scale-${color}`);
        div.textContent = Math.abs(num);
        if (icon) {
          div.classList.add('has-icon');
          div.style.setProperty('--fa-icon', `"\\${icon}"`);
        }
        break;
      }
    }
  },

  parseCondition(rule) {
    return (value) => {
      const comparisons = String(rule || '').split(/\s*(\&\&|\|\|)\s*/);
      const evaluate = (part) => {
        const match = String(part).trim().match(/^(>=|<=|>|<|==|!=)\s*(-?\d+(?:\.\d+)?)$/);
        if (!match) return false;
        const expected = Number(match[2]);
        return ({ '>': value > expected, '>=': value >= expected, '<': value < expected, '<=': value <= expected, '==': value === expected, '!=': value !== expected })[match[1]];
      };
      let result = evaluate(comparisons[0]);
      for (let index = 1; index < comparisons.length; index += 2) result = comparisons[index] === '&&' ? result && evaluate(comparisons[index + 1]) : result || evaluate(comparisons[index + 1]);
      return result;
    };
  }

};

/**
 * Adiciona os métodos ao protótipo de HTMLTableCellElement
 */
const extendHTMLTableCellElement = () => {
  Object.entries(htmlTableCellElementExtensions).forEach(([key, value]) => {
    if (!HTMLTableCellElement.prototype[key]) {
      HTMLTableCellElement.prototype[key] = value;
    } else {
      console.warn(`HTMLTableCellElement.prototype.${key} já existe e não será sobrescrito.`);
    }
  });
};

export default extendHTMLTableCellElement;
